import React, { useState } from 'react';
import {
  Title,
  Card,
  Stack,
  Group,
  Button,
  Text,
  ActionIcon,
  Modal,
  TextInput,
  Select,
  Badge,
  Loader,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { useTranslation } from 'react-i18next';
import { Profession, ProfessionFamily } from '../../types';

export const Professions: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const familyOptions = [
    { value: 'engineering', label: t('professions.familyEngineering') },
    { value: 'qa', label: t('professions.familyQa') },
    { value: 'devops', label: t('professions.familyDevops') },
    { value: 'product', label: t('professions.familyProduct') },
    { value: 'other', label: t('professions.familyOther') },
  ];
  const familyLabel = (f?: ProfessionFamily) =>
    familyOptions.find((o) => o.value === (f || 'other'))?.label ?? '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editProfession, setEditProfession] = useState<Profession | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [family, setFamily] = useState<ProfessionFamily>('other');

  // Получение профессий с бэкенда
  const { data: professions = [], isLoading } = useQuery({
    queryKey: ['professions'],
    queryFn: async () => api('/professions'),
    staleTime: 5 * 60 * 1000,
  });

  // Мутация создания
  const createProfessionMutation = useMutation({
    mutationFn: async (data: { name: string; family: ProfessionFamily }) =>
      api('/professions', { method: 'POST', body: data }),
    onSuccess: () => {
      showToast({
        title: t('professions.added'),
        message: t('professions.addedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['professions'] });
      setModalOpen(false);
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('professions.addError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Мутация редактирования
  const editProfessionMutation = useMutation({
    mutationFn: async (data: Profession) =>
      api(`/professions/${data._id}`, {
        method: 'PATCH',
        body: { name: data.name, family: data.family },
      }),
    onSuccess: () => {
      showToast({
        title: t('professions.updated'),
        message: t('professions.updatedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['professions'] });
      setModalOpen(false);
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('professions.editError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Мутация удаления
  const deleteProfessionMutation = useMutation({
    mutationFn: async (_id: string) => api(`/professions/${_id}`, { method: 'DELETE' }),
    onSuccess: () => {
      showToast({
        title: t('professions.deleted'),
        message: t('professions.deletedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['professions'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('professions.deleteError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleAdd = () => {
    setEditProfession(null);
    setInputValue('');
    setFamily('other');
    setModalOpen(true);
  };

  const handleEdit = (profession: Profession) => {
    setEditProfession(profession);
    setInputValue(profession.name);
    setFamily(profession.family || 'other');
    setModalOpen(true);
  };

  const handleDeleteProfession = (profession: Profession) => {
    modals.openConfirmModal({
      title: t('professions.deleteConfirmTitle', { name: profession.name }),
      centered: true,
      children: (
        <Text
          size="sm"
          dangerouslySetInnerHTML={{
            __html: t('professions.deleteConfirmText', { name: profession.name }),
          }}
        />
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        deleteProfessionMutation.mutate(profession._id);
      },
    });
  };

  const handleSave = () => {
    if (inputValue.trim() === '') return;
    if (editProfession) {
      editProfessionMutation.mutate({ _id: editProfession._id, name: inputValue, family });
    } else {
      createProfessionMutation.mutate({ name: inputValue, family });
    }
  };

  return (
    <PageContainer>
      <Group justify="space-between" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('professions.title')}</Title>
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={handleAdd}>
          {t('professions.add')}
        </Button>
      </Group>
      <Stack gap="md" style={{ maxWidth: 600 }}>
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : professions.length === 0 ? (
          <Text ta="center" py="xl">
            {t('professions.empty')}
          </Text>
        ) : (
          professions.map((profession: Profession) => (
            <Card key={profession._id} shadow="sm" padding="md" radius="md" withBorder>
              <Group justify="space-between">
                <Group gap="sm">
                  <Text size="md">{profession.name}</Text>
                  <Badge variant="light" color="gray">
                    {familyLabel(profession.family)}
                  </Badge>
                </Group>
                <Group gap="xs">
                  <ActionIcon variant="light" color="teal" onClick={() => handleEdit(profession)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleDeleteProfession(profession)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))
        )}
      </Stack>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editProfession ? t('professions.edit') : t('professions.add')}
      >
        <Stack>
          <TextInput
            label={t('professions.name')}
            placeholder={t('professions.placeholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
          />
          <Select
            label={t('professions.family')}
            description={t('professions.familyHint')}
            data={familyOptions}
            value={family}
            onChange={(v) => setFamily((v as ProfessionFamily) || 'other')}
            allowDeselect={false}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setModalOpen(false)} color="gray">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} color="teal">
              {editProfession ? t('common.save') : t('common.add')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
};
